import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import moment from 'moment-timezone'
import { AlertTriangle, GraduationCap, ChevronDown, ChevronUp, CheckCircle2, Circle, CircleDot } from 'lucide-react'
import { uniq } from 'lodash'

import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Badge } from '../../ui/badge'
import { Progress } from '../../ui/progress'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import UploadButton from '../UploadButton/UploadButton'
import StudentEditForm from './StudentEditForm'
import SkillButton from '../SkillButton/SkillButton'
import SkillSelector from '../SkillSelector/SkillSelector'
import { requestCreator } from '../../../utilities/requests'
import { getNextSkillStatus, updateStudentSkillStatus } from '../../../utilities/studentsSkills'
import { cn } from '../../../lib/utils'

const { get: getStudent, cancel: cancelStudentRequest } = requestCreator()

function SkillProgressSection({
  student,
  className,
  onMarkSkill,
  isSkillLoading,
  skillErrorMessage,
  renderSkillUpdatedText
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')

  const skills = student?.studentsSkills || []
  const currentSkillLevel = student?.currentSkillLevel

  const skillCategories = skills.length > 0 ? uniq(skills.map((s) => s.category)) : []

  useEffect(() => {
    if (skillCategories.length === 0) {
      setSelectedCategory('')
      return
    }

    if (!selectedCategory || !skillCategories.includes(selectedCategory)) {
      setSelectedCategory(skillCategories[0])
    }
  }, [skillCategories, selectedCategory])

  if (!currentSkillLevel) {
    return null
  }

  if (skills.length === 0) {
    return (
      <div className={cn('mt-6 pt-6 border-t border-gray-200', className)}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-600" />
            <h4 className="text-sm font-semibold text-gray-900">Skill Progress</h4>
          </div>
          <Badge className="bg-purple-600 hover:bg-purple-700">{currentSkillLevel.name}</Badge>
        </div>
        <p className="mt-3 text-sm text-gray-600">No skills assigned yet.</p>
      </div>
    )
  }

  const completedSkills = skills.filter((s) => s.status === 'completed')
  const progressPercentage = Math.round((completedSkills.length / skills.length) * 100)

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />
      case 'introduced':
        return <CircleDot className="w-4 h-4 text-blue-600" />
      default:
        return <Circle className="w-4 h-4 text-gray-300" />
    }
  }

  const categorySkills = skills
    .filter((skill) => skill.category === selectedCategory)
    .sort((a, b) => a.ordinal - b.ordinal)

  const showSkillSelector = skillCategories.length > 1

  return (
    <div className={cn('mt-6 pt-6 border-t border-gray-200', className)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-purple-600" />
          <h4 className="text-sm font-semibold text-gray-900">Skill Progress</h4>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-purple-600 hover:bg-purple-700">{currentSkillLevel.name}</Badge>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>

      <div className="mt-3">
        <div className="flex items-center gap-3 mb-2">
          <Progress value={progressPercentage} className="flex-1 h-2" />
          <span className="text-sm font-semibold text-purple-600 min-w-[3rem] text-right">{progressPercentage}%</span>
        </div>
        <p className="text-xs text-gray-600">
          {completedSkills.length} of {skills.length} skills completed
          {progressPercentage === 100 && ' - Ready for next level!'}
        </p>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          {showSkillSelector && (
            <div className="flex justify-center pb-3 border-b border-gray-100">
              <SkillSelector
                skillCategories={skillCategories}
                selectedCategory={selectedCategory}
                onCategorySelected={setSelectedCategory}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg text-xs">
            <div className="flex items-center gap-1">
              <Circle className="w-3 h-3 text-gray-300" />
              <span className="text-gray-600">Not Started</span>
            </div>
            <div className="flex items-center gap-1">
              <CircleDot className="w-3 h-3 text-blue-600" />
              <span className="text-gray-600">Introduced</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              <span className="text-gray-600">Completed</span>
            </div>
          </div>

          <div className="space-y-2">
            {categorySkills.map((skill) => {
              const { id, shortLabel, name, status, statusUpdatedBy, statusUpdatedAt } = skill

              return (
                <div key={id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white">
                  <div className="flex-shrink-0 pt-1">{getStatusIcon(status)}</div>
                  <SkillButton
                    label={shortLabel}
                    state={status}
                    onClick={() => onMarkSkill(skill)}
                    loading={isSkillLoading(id)}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-900 font-medium block truncate">{name}</span>
                    <span className="text-xs text-gray-500">
                      {renderSkillUpdatedText(statusUpdatedBy, statusUpdatedAt)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
          {skillErrorMessage && <p className="text-sm text-red-600">{skillErrorMessage}</p>}
        </div>
      )}
    </div>
  )
}

SkillProgressSection.propTypes = {
  student: PropTypes.shape({
    studentsSkills: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        shortLabel: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        category: PropTypes.string,
        status: PropTypes.string.isRequired,
        ordinal: PropTypes.number.isRequired
      })
    ),
    currentSkillLevel: PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  }),
  className: PropTypes.string,
  onMarkSkill: PropTypes.func.isRequired,
  isSkillLoading: PropTypes.func.isRequired,
  skillErrorMessage: PropTypes.string,
  renderSkillUpdatedText: PropTypes.func.isRequired
}

function StudentDrawer({
  open,
  onClose,
  studentId = null,
  facilityId = null,
  onUpdate,
  onDelete,
  onActionSuccess,
  onActionError,
  canManageStudentDetails = true,
  canDeleteStudent = true,
  facilityTimeZone = null,
  allowLevelEditing = true
}) {
  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [submitForm, setSubmitForm] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [skillErrorMessage, setSkillErrorMessage] = useState('')
  const [updatingSkillIds, setUpdatingSkillIds] = useState([])

  useEffect(() => {
    if (open && studentId && facilityId) {
      setIsLoading(true)
      setError(false)
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setSkillErrorMessage('')
      setUpdatingSkillIds([])
      getStudent(`/api/facilities/${facilityId}/users/${studentId}`, {
        params: {
          include:
            'address,guardians,skate_rental,current_skill_level,klasses_as_student,klasses_as_student.coaches,students_skills'
        }
      })
        .then((studentData) => {
          setStudent(studentData)
          setIsLoading(false)
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setIsLoading(false)
            setError(true)
          }
        })
    }

    return () => {
      cancelStudentRequest()
    }
  }, [open, studentId, facilityId])

  function handleClose() {
    setStudent(null)
    setError(false)
    setIsDeleting(false)
    setShowDeleteConfirm(false)
    setSkillErrorMessage('')
    setUpdatingSkillIds([])
    onClose()
  }

  async function handleDelete() {
    setIsDeleting(true)
    try {
      await axios.delete(`/api/facilities/${facilityId}/users/${studentId}`)
      if (onDelete) {
        onDelete(studentId)
      }
      if (onActionSuccess) onActionSuccess('Student deleted successfully')
      handleClose()
    } catch (err) {
      setError(true)
      if (onActionError) onActionError('Could not delete student. Try again.')
      setIsDeleting(false)
    }
  }

  function handleUpdateSuccess(updatedStudent) {
    getStudent(`/api/facilities/${facilityId}/users/${studentId}`, {
      params: {
        include:
          'address,guardians,skate_rental,current_skill_level,klasses_as_student,klasses_as_student.coaches,students_skills'
      }
    })
      .then((studentData) => {
        setStudent(studentData)
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setError(true)
        }
      })

    if (onUpdate) {
      onUpdate(updatedStudent)
    }
    if (onActionSuccess) onActionSuccess('Student saved successfully')
  }

  async function handlePhotoUpload(newPublicId) {
    try {
      await axios.patch(`/api/facilities/${facilityId}/users/${studentId}`, {
        user: { cloudinary_photo_public_id: newPublicId }
      })
      setStudent((prev) => ({ ...prev, cloudinaryPhotoPublicId: newPublicId }))
      if (onUpdate) {
        onUpdate({ id: studentId, attributes: { cloudinaryPhotoPublicId: newPublicId } })
      }
    } catch (err) {
      setError(true)
      if (onActionError) onActionError('Could not update student photo. Try again.')
    }
  }

  function formatTimestamp(value) {
    if (!value) return null
    const zone = facilityTimeZone || moment.tz.guess()
    return moment(value).tz(zone).format('M/D @ h:mmA z')
  }

  function formatUpdatedBy(user) {
    if (!user) return 'Unknown'
    return user.preferredName || user.fullName || 'Unknown'
  }

  function renderSkillUpdatedText(user, timestamp) {
    const formattedTimestamp = formatTimestamp(timestamp)
    if (!formattedTimestamp) return 'No recent update'
    return `Updated by ${formatUpdatedBy(user)} on ${formattedTimestamp}`
  }

  function isSkillLoading(skillId) {
    return updatingSkillIds.includes(String(skillId))
  }

  async function handleMarkSkill(skill) {
    const nextStatus = getNextSkillStatus(skill.status)
    const skillId = String(skill.id)

    setSkillErrorMessage('')
    setUpdatingSkillIds((previous) => [...previous, skillId])

    try {
      const updatedSkill = await updateStudentSkillStatus(skill.id, nextStatus)
      setStudent((previous) => {
        if (!previous) return previous
        return {
          ...previous,
          studentsSkills: (previous.studentsSkills || []).map((record) =>
            String(record.id) === String(updatedSkill.id)
              ? {
                  ...record,
                  status: updatedSkill.status,
                  statusUpdatedAt: updatedSkill.statusUpdatedAt,
                  statusUpdatedBy: updatedSkill.statusUpdatedBy
                }
              : record
          )
        }
      })
      if (onUpdate) onUpdate()
    } catch (error) {
      setSkillErrorMessage('Could not update skill. Try again.')
    } finally {
      setUpdatingSkillIds((previous) => previous.filter((id) => id !== skillId))
    }
  }

  function renderContent() {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <LoadingSpinner size="1/5" />
        </div>
      )
    }

    if (error || !student) {
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gray-100">
              <AlertTriangle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {error ? 'Unable to Load Student' : 'No Student Information'}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {error
                ? 'There was an error loading the student details. Please try again or contact support if the problem persists.'
                : 'No information is available for this student at the moment.'}
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button variant="outline" onClick={handleClose} size="lg">
              Close
            </Button>
          </DrawerFooter>
        </>
      )
    }

    if (showDeleteConfirm) {
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-red-100">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Delete Student?</h3>
            <p className="text-sm text-gray-500 max-w-sm mb-6">
              Are you sure you want to delete {student.preferredName || student.fullName}? This action cannot be undone.
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button variant="destructive" onClick={handleDelete} size="lg" disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Yes, Delete Student'}
            </Button>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} size="lg" disabled={isDeleting}>
              Cancel
            </Button>
          </DrawerFooter>
        </>
      )
    }

    const { fullName, preferredName, cloudinaryPhotoPublicId } = student

    return (
      <>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col items-center space-y-3 mb-6 pb-6">
            {canManageStudentDetails && (
              <UploadButton
                cloudinaryPhotoPublicId={cloudinaryPhotoPublicId}
                onPhotoUpload={handlePhotoUpload}
                fullName={fullName || preferredName}
                userId={student.id}
                isStudent
              />
            )}
          </div>
          <div className="mb-6 pb-3 px-3 border border-gray-200 rounded-lg">
            <SkillProgressSection
              student={student}
              className="border-none mt-0"
              onMarkSkill={handleMarkSkill}
              isSkillLoading={isSkillLoading}
              skillErrorMessage={skillErrorMessage}
              renderSkillUpdatedText={renderSkillUpdatedText}
            />
          </div>

          {canManageStudentDetails && (
            <StudentEditForm
              student={student}
              facilityId={facilityId}
              allowLevelEditing={allowLevelEditing}
              onSuccess={handleUpdateSuccess}
              onError={onActionError}
              onSubmitExposed={(submitFn) => setSubmitForm(() => submitFn)}
              onSubmittingChange={setIsSubmitting}
            />
          )}

          {student.klassesAsStudent && student.klassesAsStudent.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Enrolled Classes</h4>
              <div className="space-y-2">
                {student.klassesAsStudent.map((klass) => (
                  <div key={klass.id} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-sm text-gray-900">
                      {klass.dayOfWeek} - {klass.timeOfDay}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {klass.startDate} to {klass.endDate}
                    </p>
                    {klass.coaches && klass.coaches.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Coaches: {klass.coaches.map((coach) => coach.preferredName).join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-200 space-y-2">
          {canManageStudentDetails && (
            <div className="flex gap-2 w-full">
              <Button onClick={submitForm} size="lg" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
              </Button>
              {canDeleteStudent && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  size="lg"
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Delete Student
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" onClick={handleClose} size="lg" className="w-full" disabled={isSubmitting}>
            Close
          </Button>
        </DrawerFooter>
      </>
    )
  }

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} direction="right">
      <DrawerContent className="max-h-[96vh] md:max-h-screen">
        <DrawerHeader className="border-b border-gray-200">
          <DrawerTitle className="text-xl">Student Details</DrawerTitle>
          <DrawerDescription>View and manage student information</DrawerDescription>
        </DrawerHeader>
        {renderContent()}
      </DrawerContent>
    </Drawer>
  )
}

StudentDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onActionSuccess: PropTypes.func,
  onActionError: PropTypes.func,
  canManageStudentDetails: PropTypes.bool,
  canDeleteStudent: PropTypes.bool,
  facilityTimeZone: PropTypes.string,
  allowLevelEditing: PropTypes.bool
}

export default StudentDrawer
